import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import type {
	AgentManagedExtensionAttributes,
	AgentManagedExtensionParameters,
	AgentManagedObjectMacroParam,
} from '../../../common/agentManagedExtensionPluginType';
import { generateLocalId } from '../../../common/utils';
import { getAiManagedObjectParameter } from '../../editor-commands/agent-managed-object-node';
import { COPY_PASTE_MACRO_PARAM, type CopyPasteMarker } from './types';

type MacroParams = NonNullable<AgentManagedExtensionParameters['macroParams']>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getStringValue = (value: unknown): string | undefined =>
	typeof value === 'string' && value.length > 0 ? value : undefined;

export const getLocalId = (
	attrs: AgentManagedExtensionAttributes | undefined,
): string | undefined => getStringValue(attrs?.localId);

const parseCopyPasteMarker = (value: unknown): CopyPasteMarker | undefined => {
	const rawValue = isRecord(value) && 'value' in value ? value.value : value;
	if (typeof rawValue !== 'string') {
		return undefined;
	}

	try {
		const parsed: unknown = JSON.parse(rawValue);
		if (!isRecord(parsed)) {
			return undefined;
		}

		const operation = parsed.operation;
		const sourceAiManagedObjectId = parsed.sourceAiManagedObjectId;
		if (
			(operation !== 'copy' && operation !== 'cut') ||
			typeof sourceAiManagedObjectId !== 'string'
		) {
			return undefined;
		}

		const marker: CopyPasteMarker = { operation, sourceAiManagedObjectId };
		if (typeof parsed.sourcePageUrl === 'string') {
			marker.sourcePageUrl = parsed.sourcePageUrl;
		}
		if (typeof parsed.sourceReferenceObjectAri === 'string') {
			marker.sourceReferenceObjectAri = parsed.sourceReferenceObjectAri;
		}

		return marker;
	} catch {
		return undefined;
	}
};

/** Reads the temporary clipboard marker without trusting arbitrary pasted macro params. */
export const getCopyPasteMarker = (
	attrs: AgentManagedExtensionAttributes | undefined,
): CopyPasteMarker | undefined =>
	parseCopyPasteMarker(attrs?.parameters?.macroParams?.[COPY_PASTE_MACRO_PARAM]);

export const getMacroParamsWithoutCopyPasteMarker = (
	macroParams: AgentManagedExtensionParameters['macroParams'] = {},
): MacroParams => {
	const { [COPY_PASTE_MACRO_PARAM]: _marker, ...rest } = macroParams;
	return rest;
};

/** Adds the hidden marker used only while the node is serialized onto the clipboard. */
export const getAttrsWithCopyPasteMarker = ({
	attrs,
	clearAiManagedObject,
	marker,
	regenerateLocalId,
}: {
	attrs: AgentManagedExtensionAttributes;
	clearAiManagedObject: boolean;
	marker: CopyPasteMarker;
	regenerateLocalId: boolean;
}): AgentManagedExtensionAttributes => {
	const parameters = (attrs.parameters ?? {}) as AgentManagedExtensionParameters;
	const macroParams = {
		...parameters.macroParams,
		[COPY_PASTE_MACRO_PARAM]: {
			value: JSON.stringify(marker),
		},
	};

	if (clearAiManagedObject) {
		delete macroParams.aiManagedObject;
	}

	return {
		...attrs,
		localId: regenerateLocalId ? generateLocalId() : attrs.localId,
		parameters: {
			...parameters,
			macroParams,
		},
	};
};

/** Removes clipboard-only metadata before the pasted node is inserted into the document. */
export const getAttrsWithoutCopyPasteMarker = ({
	attrs,
	clearAiManagedObject,
	localId,
}: {
	attrs: AgentManagedExtensionAttributes;
	clearAiManagedObject: boolean;
	localId: string;
}): AgentManagedExtensionAttributes => {
	const parameters = (attrs.parameters ?? {}) as AgentManagedExtensionParameters;
	const macroParams = getMacroParamsWithoutCopyPasteMarker(parameters.macroParams);

	if (clearAiManagedObject) {
		delete macroParams.aiManagedObject;
	}

	return {
		...attrs,
		localId,
		parameters: {
			...parameters,
			macroParams,
		},
	};
};

/** Writes the newly created AIMO payload back to the block's macro params. */
export const getAttrsWithAiManagedObject = ({
	aimo,
	attrs,
}: {
	aimo: AiManagedObject;
	attrs: AgentManagedExtensionAttributes;
}): AgentManagedExtensionAttributes => {
	const parameters = (attrs.parameters ?? {}) as AgentManagedExtensionParameters;
	const macroParams = getMacroParamsWithoutCopyPasteMarker(parameters.macroParams);
	return {
		...attrs,
		parameters: {
			...parameters,
			macroParams: {
				...macroParams,
				aiManagedObject: {
					value: JSON.stringify(getAiManagedObjectParameter(aimo)),
				} satisfies AgentManagedObjectMacroParam,
			},
		},
	};
};
